import { useAuth0 } from '@/utils/auth0';
import { Browser } from '@capacitor/browser';
import { createLogoutUri } from '../utils/redirect-uri';
import { clearAnalyticsSessions } from '@/utils/clear-analytics-sessions';
import { clearOnboardingStatusFromStorage } from '@/utils/onboarding-status-storage';
import { clearNextStepsCaches } from '@/utils/clear-next-steps-caches';
import { clearFastSelection } from '@/utils/fast-selection';
import { removeAllNotesCorpora } from '@/utils/courses-notes-corpus';
import { offerConnectedAppsSignOut } from '@/utils/connected-apps-signout';
import { abortRotationOnLogout } from '@/utils/courses-encryption/logout-rotation';
import { revokeThisDeviceOnLogout } from '@/utils/courses-encryption/logout-revocation';
import { forgetAllRevisions } from '@/utils/courses-encryption/annotation-revisions';
import { forgetFirstBlankTriggers } from '@/utils/courses-encryption/enrol-on-first-blank';
import { emitAnnotationTelemetry } from '@/utils/courses-autosave/telemetry';
import { autosave, outbox } from '@/utils/courses-autosave';
import { clearAllDrafts as clearBmtDrafts } from '@/utils/bmt-drafts';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

/**
 * How long sign-out waits for a queued Next Steps edit to reach the server
 * before asking the member what to do with it. Longer than the engine's own
 * 3 s bound, and deliberately: sign-out is the one flush point where "could
 * not be sent" means the words are GONE — decision 9.3 (rewritten 2026-09-15)
 * sweeps the whole queue at sign-out, sealed or not, because a member's words
 * do not stay on a phone they have signed out of. A member who chose to sign
 * out will wait a few seconds to keep what they wrote. Only ever waited when
 * something IS unsent; the ordinary sign-out has an empty queue and is as
 * instant as before.
 */
export const SIGN_OUT_FLUSH_TIMEOUT_MS = 8000;
/** The "Signing out…" line's moving dots, one step per tick. */
export const SIGN_OUT_DOTS_INTERVAL_MS = 400;

/**
 * Send what is queued, and show that this is what the wait is for: the Sign Out
 * row gives no other sign that the tap landed, and the wait can run to seconds
 * on a poor connection. Resolves with how many rows are STILL unsent afterwards,
 * which is the number the decision below is about.
 *
 * Takes the component rather than being a method, so the existing specs' bare
 * `Logout.methods.logoutUser.call(vm)` keeps working on a plain object.
 *
 * @param {object} vm the component using this mixin
 * @returns {Promise<number>}
 */
async function flushBeforeSignOut(vm) {
  let dots = null;
  if (autosave.pendingKeys().length) {
    // `signOutDots > 0` is what the host reads as "waiting"; 1..3 is the dot count.
    vm.signOutDots = 1;
    dots = setInterval(() => {
      vm.signOutDots = (vm.signOutDots % 3) + 1;
    }, SIGN_OUT_DOTS_INTERVAL_MS);
  }
  try {
    await autosave.flush(undefined, { timeoutMs: SIGN_OUT_FLUSH_TIMEOUT_MS });
  } catch (error) {
    console.warn('[Logout] Autosave flush failed:', error);
  } finally {
    clearInterval(dots);
    vm.signOutDots = 0;
  }
  return autosave.pendingKeys().length;
}

/**
 * Ask whether to sign out with `count` edits unsent. Resolves `true` to go on
 * and `false` to stay signed in — in which case nothing is torn down, the edits
 * stay queued, and the queue keeps retrying them on its own (backoff, the
 * network returning, the app coming back to the foreground), so the next tap
 * on Sign Out is instant once they have landed.
 *
 * The question is put by the host page's `SignOutUnsavedModal`, bound to this
 * mixin's state and to `resolveUnsavedSignOut`. A host without one — the
 * register page's forced sign-outs, a spec's bare `vm` — cannot ask, and signs
 * out as it always has.
 *
 * @param {object} vm
 * @param {number} count
 * @returns {Promise<boolean>}
 */
function confirmSignOutWithUnsaved(vm, count) {
  if (!vm.$refs?.signOutUnsavedModal) return Promise.resolve(true);
  return new Promise(resolve => {
    vm.unsavedSignOutCount = count;
    // Pending resolver IS the open modal: `showUnsavedSignOutModal` derives from it.
    vm.unsavedSignOutResolver = resolve;
  });
}

export default {
  data() {
    const { logout } = useAuth0();
    return {
      logout,
      /** A sign-out is running: the row stays tappable, and a second tap must not start a second one. */
      signingOut: false,
      /** 0 while nothing is being sent; 1..3 while the queue is, for the moving dots. */
      signOutDots: 0,
      /** The unsaved-edits question, when the flush above was not enough. */
      unsavedSignOutCount: 0,
      unsavedSignOutResolver: null,
    };
  },
  computed: {
    /** The queue is being sent ahead of the teardown — the host shows "Signing out…" meanwhile. */
    signOutWaiting() {
      return this.signOutDots > 0;
    },
    /** "." → ".." → "...", one step per tick, after a stationary "Signing out". */
    signOutEllipsis() {
      return '.'.repeat(this.signOutDots);
    },
    /** The question is open exactly while an answer is awaited. */
    showUnsavedSignOutModal() {
      return this.unsavedSignOutResolver !== null;
    },
  },
  methods: {
    async logoutUser() {
      // One sign-out at a time: the row stays tappable while the flush below
      // waits, and a second tap would start a second teardown beside the first.
      if (this.signingOut) return;
      this.signingOut = true;
      try {
        // FIRST of the teardown, and for two reasons that both put it here rather
        // than anywhere below: sealing a queued edit needs MK, which
        // `courses-keys/wipe` is about to take away, and the sweeps below must not
        // race a send. Bounded (`SIGN_OUT_FLUSH_TIMEOUT_MS`) because signing out
        // must never hang on a dead network. Never able to fail the sign-out.
        const unsent = await flushBeforeSignOut(this);
        // What the bound could not send is not dropped silently: the sweep below
        // takes the whole queue (plan §9.3), so the member is asked first, and
        // "cancel" leaves them signed in with the edits still queued and retrying.
        if (unsent > 0 && !(await confirmSignOutWithUnsaved(this, unsent))) return;

        // The annotation write path's summary for this session (plan §8.5), before
        // `clearAnalyticsSessions` below drops the RUM user — an action added after
        // it is still recorded but no longer attributed to the member it describes.
        // After the flush, so the requests it just sent are counted.
        try {
          emitAnnotationTelemetry('logout');
        } catch (error) {
          console.warn('[Logout] Annotation telemetry emit failed:', error);
        }

        // One contract for every store access below: `this.$store` itself may be
        // absent, but a store that IS present is a real Vuex store. So `?.` guards
        // the store and never its methods — `commit` and `dispatch` are not
        // optional on something that answered the first guard (#1524).
        //
        // The `state?.user?.token` chain on the next line is the exception, and a
        // different question: it guards the SHAPE of the state, because the `user`
        // module need not be registered on a store that exists.
        //
        // Before anything is torn down, and never blocking: offer to also end the
        // member's SSO sessions in sister apps (ADR-0014 R3). Has to come first — the
        // revoke-all call needs the token this flow is about to invalidate.
        await offerConnectedAppsSignOut({
          token: this.$store?.state?.user?.token || localStorage.getItem('token'),
        });

        // Clear sessions for analytics services
        await clearAnalyticsSessions();

        // Clear onboarding status from localStorage
        clearOnboardingStatusFromStorage();

        // An enrolled member's notes drain into an IndexedDB corpus, one database
        // per member (#1548): the same words again, so it goes too, for every
        // member this phone has held. Before the sweep below, which takes the
        // registry the removal reads with it. Bounded and never throwing: a
        // deletion another context blocks must not hold sign-out up, and a
        // plaintext member has no corpus, so for them this is a no-op.
        await removeAllNotesCorpora();

        // Clear the Next Steps read-caches and blank answers: a member's own
        // words, which must not outlive their session on a shared phone.
        clearNextStepsCaches();
        clearFastSelection();

        // Decision 9.3 (rewritten 2026-09-15): the member's whole autosave queue
        // goes, sealed entries and plaintext alike. The flush above sent what it
        // could and the member was asked about the rest, so nothing here is a
        // surprise — and a member's words do not stay on a phone they have
        // signed out of, sealed or not, exactly as the corpus above does not.
        // Before `courses-keys/wipe`, with the other Next Steps sweeps. See the
        // enumeration in `logout-clears-next-steps.spec.js`, which fails if
        // anything of theirs starts outliving a sign-out.
        try {
          const memberId = this.$store?.state?.profile?.id ?? null;
          if (memberId !== null) await outbox.removeMember(memberId);
        } catch (error) {
          console.warn('[Logout] The autosave outbox was not removed:', error);
        }
        // And the engine's slots, AFTER the outbox: a row still debouncing or
        // backing off would otherwise fire after this member is gone, write
        // their words back into the outbox just emptied, and send under whatever
        // token was left. Not `reset()` — that also drops the listeners
        // `installAutosave` registered for the life of the process.
        autosave.cancelAll();

        // ...and the store's copy of the same words. Clearing storage alone is
        // only half of it: `Browser.open`'s `windowName` is "Web only ... Ignored
        // on other platforms", and the iOS side presents an
        // SFSafariViewController overlay, so on native — the only platform Next
        // Steps ships notes on — this document survives the sign-out below, and
        // every Vuex module in it with it. Left in place, `courses/allNotes` is
        // what `fetchAllNotes` paints from its offline fallback for whoever logs
        // in next, and `setUserAnswer` serialises the whole answer map on its
        // first write, putting the previous member's answers back into the
        // storage just cleared.
        // Tell the API this device is done BEFORE anything below is wiped (spec
        // 6.2, 6.6): the revoke is a signed operation, the signing key is wrapped
        // under MK, and `courses-devices/reset` drops the status snapshot the
        // wrapped signing key is read from — so this has to come first, not
        // after the wipe as 6.2's prose has it. `revokeThisDeviceOnLogout` is
        // written never to reject and never to outlive its own timeout; the
        // catch is belt and braces on top of that. Nothing this call could do
        // may stop the wipe below from running: a stale device row is a
        // nuisance, key material left on the phone is what sign-out is for.
        if (this.$store) {
          // A rotation this device is running, abandoned while MK2 and the status
          // snapshot are both still here — after this line neither is. It does
          // not block and does not refuse: see `logout-rotation.js` for why
          // sign-out must never be the thing that stops a member leaving.
          await abortRotationOnLogout(this.$store).catch(() => {});
          await revokeThisDeviceOnLogout(this.$store).catch(() => {});
        }

        this.$store?.commit('courses/reset');
        this.$store?.commit('courses-devices/reset');
        // Whatever the queue was still saying about this member's rows.
        this.$store?.commit('courses-autosave/reset');
        // The BF2026 pledge: this member's pledge, eligibility and what they just
        // submitted. Individual giving data, so it must not survive to the next
        // member on a shared phone (the done page and the banner both read it).
        this.$store?.commit('buildingFundPledge/reset');
        // The AGM RSVP: whether this member is eligible and whether they are attending.
        // Member data that must not be on screen for the next member on a shared phone.
        this.$store?.commit('agm/reset');
        // And a one-time pledge's amount waiting for Give now (myGiving has no reset).
        this.$store?.commit('myGiving/setGiveNowPrefill', null);
        // BMT: the enrolment and homework this member saw, and any unsent homework
        // answer (it can hold a testimony). A fresh `me` would otherwise be served
        // to the next member for up to a minute.
        this.$store?.commit('bmt/RESET_STATE');
        await clearBmtDrafts();
        // The annotation revisions this session learned (#1584). Not state, so
        // no module reset reaches them: a number kept across a sign-out would be
        // sent as the next member's `expectedRevision` on a shared phone.
        forgetAllRevisions();
        // And which members this session has already asked to enrol from a
        // blank. Session state like the revisions above, not module state, so
        // nothing else reaches it — and left behind it would stop the NEXT
        // member on a shared phone enrolling from the only surface they use.
        forgetFirstBlankTriggers();
        // The flags this member was targeted with (a tester's Y21 door, say), for the same reason:
        // the document, and the flags in it, outlive the sign-out on native.
        useFeatureFlags().forgetMemberFlags();

        // ...and the member's Next Steps key material (spec section 6.2, "Logout
        // wipes"): MK and the device key leave IndexedDB and the courses-keys
        // module, so the next member on this phone cannot open the previous
        // member's notes. The platform copy is not touched; it belongs to the
        // phone's owner, like a saved password. A failure here must not keep a
        // member signed in, so it is logged and the sign-out goes on.
        try {
          await this.$store?.dispatch('courses-keys/wipe');
        } catch (error) {
          console.warn('[Logout] Next Steps key wipe failed:', error);
        }

        if (process.env.VUE_APP_NODE_ENV === 'local') {
          localStorage.removeItem('token');
          // Also clear the store copy. The Auth0 path below leaves the app via a
          // redirect, so its store is discarded with the page; local mode stays in
          // the same document, and every store module reads its bearer from
          // rootState.user.token — leaving it set means a "logged out" app can
          // still issue authenticated requests.
          this.$store?.commit('user/setToken', null);
          this.$router.push('/login');
        } else {
          await this.logout({
            logoutParams: {
              returnTo: createLogoutUri(
                process.env.VUE_APP_PACKAGE_ID,
                process.env.VUE_APP_AUTH0_DOMAIN,
              ),
            },
            openUrl: async url =>
              await Browser.open({
                url,
                windowName: '_self',
              }),
          });
        }
      } finally {
        this.signingOut = false;
      }
    },
    /**
     * The unsaved-edits modal's answer. `proceed` true continues the sign-out,
     * false leaves the member signed in. Idempotent: the modal can answer once
     * by button and once more by the dismiss that follows, and only the first
     * counts.
     *
     * @param {boolean} proceed
     */
    resolveUnsavedSignOut(proceed) {
      const resolve = this.unsavedSignOutResolver;
      this.unsavedSignOutResolver = null;
      if (resolve) resolve(proceed);
    },
    /**
     * The modal closing by any route but its buttons — a tap outside its card,
     * the Android back button. Staying signed in loses nothing, so that is the
     * answer; after a button the resolver is already gone and this is a no-op.
     */
    onUnsavedSignOutModalUpdate(open) {
      if (!open) this.resolveUnsavedSignOut(false);
    },
  },
};
